import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Drawer,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { Close, FiberManualRecord, OpenInNew } from '@mui/icons-material';
import { colors } from '../../theme';
import { useTranslation } from 'react-i18next';

const AlertsTableView = ({ alerts, getPriorityColor, formatTimeAgo }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [sortBy, setSortBy] = useState('timestamp');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedAlert, setSelectedAlert] = useState(null);

  const currentLang = i18n.language === 'vi' ? 'vi' : 'en';

  const sortedAlerts = [...alerts].sort((a, b) => {
    let aVal;
    let bVal;
    switch (sortBy) {
      case 'ticker':
        aVal = a.ticker || '';
        bVal = b.ticker || '';
        break;
      case 'score':
        aVal = a.score || 0;
        bVal = b.score || 0;
        break;
      case 'priority': {
        const priorityOrder = { HIGH: 3, MEDIUM: 2, LOW: 1 };
        aVal = priorityOrder[a.priority] || 0;
        bVal = priorityOrder[b.priority] || 0;
        break;
      }
      case 'timestamp':
      default:
        aVal = new Date(a.timestamp).getTime();
        bVal = new Date(b.timestamp).getTime();
    }
    if (sortOrder === 'asc') {
      return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
    }
    return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
  });

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const handleStockClick = (ticker) => {
    setSelectedAlert(null);
    navigate(`/stocks/${ticker}`, { state: { from: `${location.pathname}${location.search}${location.hash}` } });
  };

  useEffect(() => {
    if (location.pathname !== '/alerts') {
      setSelectedAlert(null);
    }
  }, [location.pathname]);

  const SortIcon = ({ column }) => {
    if (sortBy !== column) return null;
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  const getLocalizedText = (value) => {
    if (value == null) return '-';
    if (typeof value === 'string') return value;
    if (typeof value === 'object') {
      if (Object.prototype.hasOwnProperty.call(value, currentLang) && value[currentLang] != null) return value[currentLang];
      if (Object.prototype.hasOwnProperty.call(value, 'vi') && value.vi != null) return value.vi;
      if (Object.prototype.hasOwnProperty.call(value, 'en') && value.en != null) return value.en;
      return JSON.stringify(value);
    }
    return String(value);
  };

  const formatExactTimestamp = (value) => {
    if (!value) return '-';
    const dateValue = new Date(value);
    if (Number.isNaN(dateValue.getTime())) return '-';

    return new Intl.DateTimeFormat(currentLang === 'vi' ? 'vi-VN' : 'en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(dateValue);
  };

  const getAlertContextInsights = (alert) => {
    if (Array.isArray(alert?.context_insights)) return alert.context_insights;
    if (Array.isArray(alert?.metadata?.context_insights)) return alert.metadata.context_insights;
    return [];
  };

  const stripLegacyInsightMetrics = (text) => {
    if (!text || typeof text !== 'string') return text;
    return text
      .replace(/\s*\((?=[^)]*\d)[^)]*\)/g, '')
      .replace(/\s*[+-]?\d+(?:[.,]\d+)?%/g, '')
      .replace(/\s{2,}/g, ' ')
      .trim();
  };

  const getContextInsightDisplayText = (item) => {
    if (!item) return '-';
    if (item?.display_text) return getLocalizedText(item.display_text);
    if (item?.key) {
      const translationKey = `alerts.contextSummary.${item.key}`;
      const translated = t(translationKey);
      if (translated !== translationKey) return translated;
    }
    return stripLegacyInsightMetrics(getLocalizedText(item?.text));
  };

  const getReasonDisplayText = (reason) => {
    if (reason?.display_text) return getLocalizedText(reason.display_text);
    const text = getLocalizedText(reason);
    if (!text || text === '-') return text;

    const aliases = i18n.getResource(currentLang, 'common', 'alerts.reasonAliases') || {};
    const matched = Object.values(aliases).find((alias) => (
      Array.isArray(alias?.prefixes)
        && typeof alias?.label === 'string'
        && alias.prefixes.some((prefix) => typeof prefix === 'string' && text.toLowerCase().startsWith(prefix.toLowerCase()))
    ));
    if (matched) return matched.label;

    return stripLegacyInsightMetrics(text)
      .replace(/\s+-\s+/g, ' - ')
      .trim();
  };

  const getInsightToneColor = (tone) => {
    if (tone === 'positive') return colors.success.main;
    if (tone === 'negative') return colors.error.main;
    return colors.warning.main;
  };

  const getAlertHighlights = (alert) => {
    if (Array.isArray(alert?.highlights) && alert.highlights.length) {
      return alert.highlights
        .map((item) => ({
          text: getLocalizedText(item?.text),
          tone: item?.tone || 'neutral',
          kind: item?.kind || 'context',
        }))
        .filter((item) => item.text && item.text !== '-');
    }
    const contextInsights = getAlertContextInsights(alert).map((item) => ({
      text: getContextInsightDisplayText(item),
      tone: item?.tone || 'neutral',
      kind: 'context',
    }));
    const reasons = (alert?.reasons || []).map((reason) => ({
      text: getReasonDisplayText(reason),
      tone: 'positive',
      kind: 'reason',
    }));
    return [...contextInsights, ...reasons].filter((item) => item.text && item.text !== '-');
  };

  const getAlertDisplay = (alert) => {
    if (alert?.display) {
      return {
        priority: {
          label: getLocalizedText(alert.display.priority?.label),
          color: alert.display.priority?.color || '#64748b',
        },
        decision: {
          label: getLocalizedText(alert.display.decision?.label),
          hint: getLocalizedText(alert.display.decision?.hint),
          color: alert.display.decision?.color || '#94a3b8',
          bg: alert.display.decision?.bg || alpha(alert.display.decision?.color || '#94a3b8', 0.1),
          border: alert.display.decision?.border || alpha(alert.display.decision?.color || '#94a3b8', 0.18),
        },
        signalSummary: {
          label: getLocalizedText(alert.display.signalSummary?.label),
          sub: getLocalizedText(alert.display.signalSummary?.sub),
          color: alert.display.signalSummary?.color || '#64748b',
          tooltipOverview: getLocalizedText(alert.display.signalSummary?.tooltipOverview),
          tooltipItems: (alert.display.signalSummary?.tooltipItems || []).map((item) => ({
            ...item,
            label: getLocalizedText(item.label),
            value: getLocalizedText(item.value),
          })),
        },
        type: {
          label: getLocalizedText(alert.display.type?.label),
          description: getLocalizedText(alert.display.type?.description),
          accent: alert.display.type?.accent || { color: '#64748b', bg: alpha('#64748b', 0.08) },
        },
        session: alert.display.session || null,
        summary: getLocalizedText(alert.display.summary?.text),
        time: {
          exactDate: getLocalizedText(alert.display.time?.exactDate),
        },
      };
    }

    return {
      priority: {
        label: getPriorityLabel(alert?.priority),
        color: getPriorityColor(alert?.priority),
      },
      decision: {
        label: '-',
        hint: '',
        color: '#94a3b8',
        bg: alpha('#94a3b8', 0.1),
        border: alpha('#94a3b8', 0.18),
      },
      signalSummary: {
        label: '-',
        sub: '',
        color: '#64748b',
        tooltipOverview: '',
        tooltipItems: [],
      },
      type: {
        label: getAlertTypeLabel(alert?.alert_type),
        description: getAlertTypeDescription(alert?.alert_type),
        accent: { color: '#64748b', bg: alpha('#64748b', 0.08) },
      },
      session: null,
      summary: '',
      time: {
        exactDate: formatExactTimestamp(alert?.timestamp),
      },
    };
  };

  const selectedDisplay = selectedAlert ? getAlertDisplay(selectedAlert) : null;

  const getAlertTypeLabel = (type) => {
    if (!type) return '-';
    const key = `alerts.types.${type}`;
    const translated = t(key);
    return translated !== key ? translated : type.replace(/_/g, ' ');
  };

  const getAlertTypeDescription = (type) => {
    if (!type) return '';
    const key = `alerts.typeDescriptions.${type}`;
    const translated = t(key);
    return translated !== key ? translated : '';
  };

  const getPriorityLabel = (priority) => {
    if (priority === 'HIGH') return t('alerts.filters.high');
    if (priority === 'MEDIUM') return t('alerts.filters.medium');
    if (priority === 'LOW') return t('alerts.filters.low');
    return priority || '-';
  };

  const getDetailedSignalGroups = (alertOrSignals) => {
    const alert = alertOrSignals && alertOrSignals.signals ? alertOrSignals : null;
    if (!Array.isArray(alert?.signal_groups) || alert.signal_groups.length === 0) {
      return [];
    }
    return alert.signal_groups.map((group) => ({
      ...group,
      label: getLocalizedText(group.label),
      items: (group.items || []).map((item) => ({
        ...item,
        meta: {
          ...item.meta,
          label: getLocalizedText(item.meta?.label),
          hint: getLocalizedText(item.meta?.hint),
          primaryValue: getLocalizedText(item.meta?.primaryValue),
          secondaryValue: getLocalizedText(item.meta?.secondaryValue),
          color: item.meta?.color || '#64748b',
        },
      })),
    }));
  };

  return (
    <>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: alpha(theme.palette.divider, 0.9),
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: '0 10px 24px rgba(15, 23, 42, 0.04), 0 1px 2px rgba(15, 23, 42, 0.04)',
        }}
      >
        <Table size="small" sx={{ width: '100%', tableLayout: 'fixed', minWidth: 1080 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8fafc' }}>
              <TableCell
                sx={{
                  fontWeight: 800,
                  fontSize: '0.68rem',
                  letterSpacing: 0.3,
                  color: '#64748b',
                  py: 1.1,
                  cursor: 'pointer',
                  userSelect: 'none',
                  whiteSpace: 'nowrap',
                  width: '28%',
                  '&:hover': { bgcolor: 'transparent' },
                }}
                onClick={() => handleSort('ticker')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {`${t('alerts.table.ticker')} / ${t('alerts.table.company')}`}
                  <SortIcon column="ticker" />
                </Box>
              </TableCell>
              <TableCell sx={{ fontWeight: 800, fontSize: '0.68rem', letterSpacing: 0.3, color: '#64748b', py: 1.1, width: '13%' }}>
                {t('alerts.table.signals')}
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 800,
                  fontSize: '0.68rem',
                  letterSpacing: 0.3,
                  color: '#64748b',
                  py: 1.1,
                  cursor: 'pointer',
                  userSelect: 'none',
                  whiteSpace: 'nowrap',
                  width: '11%',
                  '&:hover': { bgcolor: 'transparent' },
                }}
                onClick={() => handleSort('priority')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {t('alerts.table.priority')}
                  <SortIcon column="priority" />
                </Box>
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 800,
                  fontSize: '0.68rem',
                  letterSpacing: 0.3,
                  color: '#64748b',
                  py: 1.1,
                  cursor: 'pointer',
                  userSelect: 'none',
                  whiteSpace: 'nowrap',
                  width: '14%',
                  '&:hover': { bgcolor: 'transparent' },
                }}
                onClick={() => handleSort('score')}
                align="right"
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                  {t('alerts.table.score')}
                  <SortIcon column="score" />
                </Box>
              </TableCell>
              <TableCell sx={{ fontWeight: 800, fontSize: '0.68rem', letterSpacing: 0.3, color: '#64748b', py: 1.1, width: '21%' }}>
                {t('alerts.table.reason')}
              </TableCell>
              <TableCell sx={{ fontWeight: 800, fontSize: '0.68rem', letterSpacing: 0.3, color: '#64748b', py: 1.1, width: '9%' }}>
                {t('alerts.table.sector')}
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 800,
                  fontSize: '0.68rem',
                  letterSpacing: 0.3,
                  color: '#64748b',
                  py: 1.1,
                  cursor: 'pointer',
                  userSelect: 'none',
                  whiteSpace: 'nowrap',
                  width: '7%',
                  '&:hover': { bgcolor: 'transparent' },
                }}
                onClick={() => handleSort('timestamp')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {t('alerts.table.time')}
                  <SortIcon column="timestamp" />
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedAlerts.map((alert, idx) => {
              const display = getAlertDisplay(alert);
              const priorityColor = display.priority.color;

              return (
                <TableRow
                  key={alert.id}
                  hover
                  onClick={() => setSelectedAlert(alert)}
                  sx={{
                    bgcolor: idx % 2 === 0 ? 'white' : alpha(theme.palette.primary.main, 0.02),
                    cursor: 'pointer',
                    '&:hover': { bgcolor: idx % 2 === 0 ? 'white' : alpha(theme.palette.primary.main, 0.02) },
                    '& .MuiTableCell-root': {
                      borderBottomColor: '#eef2f7',
                    },
                  }}
                >
                <TableCell
                  sx={{
                    py: 1.1,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'transparent' },
                  }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.1 }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 800,
                            fontSize: '0.82rem',
                            color: colors.purple.main,
                            lineHeight: 1.2,
                          }}
                        >
                          {alert.ticker}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '0.74rem',
                            fontWeight: 500,
                            lineHeight: 1.4,
                            color: '#475569',
                            mt: 0.24,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            '&:hover': { color: '#334155' },
                          }}
                        >
                          {alert.company_name || '-'}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>

                  <TableCell sx={{ py: 1.1 }}>
                    {display.signalSummary.tooltipItems.length ? (
                      <Tooltip
                        title={
                          <Box sx={{ minWidth: 200 }}>
                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#fff', mb: 0.5 }}>
                              {display.signalSummary.tooltipOverview}
                            </Typography>
                            {display.signalSummary.tooltipItems.map((item) => {
                              return (
                                <Box key={item.key} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, py: 0.2 }}>
                                  <Typography sx={{ fontSize: '0.68rem', color: '#cbd5e1' }}>{item.label}</Typography>
                                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: item.color }}>
                                    {item.value}
                                  </Typography>
                                </Box>
                              );
                            })}
                          </Box>
                        }
                        arrow
                        placement="left"
                      >
                        <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.2, cursor: 'default' }}>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.45 }}>
                            <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: display.signalSummary.color }} />
                            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: display.signalSummary.color, whiteSpace: 'nowrap' }}>
                              {display.signalSummary.label}
                            </Typography>
                          </Box>
                          <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8', lineHeight: 1.2 }}>
                            {display.signalSummary.sub}
                          </Typography>
                        </Box>
                      </Tooltip>
                    ) : (
                      <Typography sx={{ fontSize: '0.72rem', color: '#cbd5e1' }}>-</Typography>
                    )}
                  </TableCell>

                  <TableCell sx={{ py: 1.1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.55 }}>
                      <Box
                        sx={{
                          width: 5,
                          height: 5,
                          borderRadius: '50%',
                          bgcolor: priorityColor,
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.72rem',
                          letterSpacing: 0.2,
                          color: priorityColor,
                        }}
                      >
                        {display.priority.label}
                      </Typography>
                    </Box>
                  </TableCell>

                  <TableCell sx={{ py: 1.1 }} align="right">
                    {alert.score !== undefined && alert.score !== null ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.16, maxWidth: 140, ml: 'auto' }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 800,
                            color: display.decision.color,
                            fontSize: '0.74rem',
                            lineHeight: 1.15,
                            whiteSpace: 'normal',
                            textAlign: 'right',
                          }}
                        >
                          {display.decision.label}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.58rem',
                            color: '#94a3b8',
                            fontWeight: 700,
                            lineHeight: 1.2,
                            whiteSpace: 'normal',
                            textAlign: 'right',
                          }}
                        >
                          {display.decision.hint}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>

                  <TableCell sx={{ py: 1.1 }}>
                    {getAlertHighlights(alert).length ? (() => {
                      const highlights = getAlertHighlights(alert);
                      const shown = highlights.slice(0, 2);
                      const extra = highlights.length - 2;
                      return (
                        <Tooltip
                          title={extra > 0 ? highlights.map((item, i) => (
                            <Box key={`${item.kind}-${i}`} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, fontSize: '0.75rem', mb: 0.35 }}>
                              <FiberManualRecord sx={{ fontSize: 5, color: getInsightToneColor(item.tone), mt: '5px', flexShrink: 0 }} />
                              <Box>{item.text}</Box>
                            </Box>
                          )) : ''}
                          arrow
                          placement="left"
                          disableHoverListener={extra <= 0}
                        >
                          <Box>
                            {shown.map((item, reasonIdx) => (
                              <Typography key={`${item.kind}-${reasonIdx}-${item.text}`} sx={{ fontSize: '0.68rem', color: '#475569', lineHeight: 1.35, display: 'flex', alignItems: 'flex-start', gap: 0.45 }}>
                                <FiberManualRecord sx={{ fontSize: 4, color: getInsightToneColor(item.tone), mt: '4px', flexShrink: 0 }} />
                                {item.text}
                              </Typography>
                            ))}
                            {extra > 0 ? (
                              <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8', mt: 0.15, cursor: 'default' }}>
                                {t('alerts.table.moreReasons', { count: extra })}
                              </Typography>
                            ) : null}
                          </Box>
                        </Tooltip>
                      );
                    })() : (
                      <Typography sx={{ fontSize: '0.72rem', color: '#cbd5e1' }}>-</Typography>
                    )}
                  </TableCell>

                  <TableCell sx={{ py: 1.1 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.76rem', fontWeight: 600, color: '#334155' }}>
                      {alert.sector_name?.[currentLang] || '-'}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ py: 1.1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', fontWeight: 600 }}>
                      {formatTimeAgo(alert.timestamp)}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Drawer
        anchor="right"
        open={Boolean(selectedAlert)}
        onClose={() => setSelectedAlert(null)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 460 },
            maxWidth: '100vw',
            bgcolor: '#fafbfc',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {selectedAlert ? (
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#fafbfc' }}>
            {(() => {
              const sessionChange = selectedDisplay?.session;
              return (
            <Box
              sx={{
                px: 1.8,
                py: 0.8,
                borderBottom: '1px solid #e2e8f0',
                bgcolor: '#f8fafc',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 0.9,
              }}
            >
              <Box sx={{ minWidth: 0, flex: 1, pr: 1 }}>
                <Typography sx={{ fontSize: '0.98rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.15 }}>
                  {selectedAlert.ticker}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.76rem',
                    color: '#475569',
                    lineHeight: 1.24,
                    mt: 0.14,
                    minWidth: 0,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {selectedAlert.company_name || '-'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.9, flexShrink: 0, minWidth: 110, justifyContent: 'flex-end' }}>
                {sessionChange?.currentPrice ? (
                  <Box sx={{ textAlign: 'right', pt: 0.05 }}>
                    <Typography sx={{ fontSize: '1.12rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.1 }}>
                      {sessionChange.currentPriceText}
                    </Typography>
                    <Typography sx={{ fontSize: '0.76rem', fontWeight: 800, color: sessionChange.color, lineHeight: 1.25, mt: 0.15 }}>
                      {sessionChange.changeText}
                    </Typography>
                  </Box>
                ) : sessionChange ? (
                  <Box sx={{ textAlign: 'right', pt: 0.16 }}>
                    <Typography sx={{ fontSize: '0.76rem', fontWeight: 800, color: sessionChange.color, lineHeight: 1.2 }}>
                      {sessionChange.formattedPct}
                    </Typography>
                  </Box>
                ) : null}
              <IconButton size="small" onClick={() => setSelectedAlert(null)} sx={{ mt: -0.15, mr: -0.35 }}>
                <Close fontSize="small" />
              </IconButton>
              </Box>
            </Box>
              );
            })()}

            <Box sx={{ p: 1.5, overflowY: 'auto', flex: 1 }}>
              <Stack spacing={1.25}>
                <Box sx={{ bgcolor: '#e2e8f0', borderRadius: 1.5, overflow: 'hidden', border: '1px solid #e2e8f0', display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '1px' }}>
                  {[
                    { label: t('alerts.detail.metrics.priority'), value: selectedDisplay?.priority.label, sub: '', color: selectedDisplay?.priority.color },
                    {
                      label: t('alerts.detail.metrics.score'),
                      value: selectedDisplay?.decision.label,
                      sub: '',
                      color: selectedDisplay?.decision.color,
                      tone: selectedDisplay?.decision,
                      inlineSub: false,
                    },
                    { label: t('alerts.detail.metrics.sector'), value: selectedAlert.sector_name?.[currentLang] || '-', sub: '', color: '#0f172a' },
                    { label: t('alerts.detail.metrics.time'), value: selectedDisplay?.time.exactDate, sub: '', color: '#0f172a' },
                  ].map((item) => (
                    <Box
                      key={item.label}
                      sx={{
                        bgcolor: '#fff',
                        px: 1.05,
                        py: 0.72,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                        gap: 0.2,
                      }}
                    >
                      <Typography sx={{ fontSize: '0.55rem', color: '#94a3b8', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.32, lineHeight: 1.1 }}>
                        {item.label}
                      </Typography>
                      {item.inlineSub ? (
                        <Box sx={{ mt: 0.12, lineHeight: 1 }}>
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              maxWidth: '100%',
                              px: 0.5,
                              py: 0.12,
                              borderRadius: 999,
                              bgcolor: item.tone?.bg || alpha(item.color, 0.08),
                              border: `1px solid ${item.tone?.border || alpha(item.color, 0.14)}`,
                              fontSize: '0.82rem',
                              color: item.color,
                              fontWeight: 800,
                              lineHeight: 1.2,
                            }}
                          >
                            {item.value}
                          </Box>
                          {item.sub ? (
                            <Typography
                              sx={{
                                mt: 0.45,
                                fontSize: '0.63rem',
                                color: '#64748b',
                                fontWeight: 700,
                                lineHeight: 1.3,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                maxWidth: '100%',
                              }}
                            >
                              {item.sub}
                            </Typography>
                          ) : null}
                        </Box>
                      ) : (
                        <>
                          <Typography sx={{ fontSize: '0.82rem', color: item.color, fontWeight: 800, mt: 0.12, lineHeight: 1.2 }}>
                            {item.value}
                          </Typography>
                          {item.sub ? (
                            <Typography sx={{ fontSize: '0.62rem', color: '#64748b', mt: 0.08 }}>
                              {item.sub}
                            </Typography>
                          ) : null}
                        </>
                      )}
                    </Box>
                  ))}
                </Box>

                <Box
                  sx={{
                    px: 1.2,
                    py: 1.0,
                    border: '1px solid #dbe7f3',
                    borderRadius: 1.5,
                    bgcolor: '#f8fbff',
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.55 }}>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.45,
                        px: 0.75,
                        py: 0.36,
                        borderRadius: 999,
                        bgcolor: selectedDisplay?.type.accent.bg,
                        flexShrink: 0,
                      }}
                    >
                      <Box
                        sx={{
                          width: 5,
                          height: 5,
                          borderRadius: '50%',
                          bgcolor: selectedDisplay?.type.accent.color,
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: '0.68rem',
                          fontWeight: 800,
                          color: selectedDisplay?.type.accent.color,
                          lineHeight: 1.2,
                        }}
                      >
                        {selectedDisplay?.type.label}
                      </Typography>
                    </Box>

                    {selectedDisplay?.type.description ? (
                      <Typography
                        sx={{
                          fontSize: '0.72rem',
                          color: '#64748b',
                          lineHeight: 1.5,
                        }}
                      >
                        {selectedDisplay?.type.description}
                      </Typography>
                    ) : null}
                  </Box>
                </Box>

                <Box sx={{ px: 1.2, py: 1.05, border: '1px solid #e2e8f0', borderRadius: 1.5, bgcolor: '#fff' }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: '#334155', mb: 0.55 }}>
                    {t('alerts.detail.sections.summary')}
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: '#475569', lineHeight: 1.55 }}>
                    {selectedDisplay?.summary}
                  </Typography>
                </Box>

                <Box sx={{ px: 1.2, py: 1.05, border: '1px solid #e2e8f0', borderRadius: 1.5, bgcolor: '#fff' }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: '#334155', mb: 0.6 }}>
                    {t('alerts.detail.sections.context')}
                  </Typography>
                  <Stack spacing={0.45}>
                    {getAlertContextInsights(selectedAlert).length ? getAlertContextInsights(selectedAlert).map((item, index) => (
                      <Box key={`${item.key || 'context'}-${index}`} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.55 }}>
                        <FiberManualRecord sx={{ fontSize: 5, color: getInsightToneColor(item.tone), mt: '6px', flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '0.73rem', color: '#475569', lineHeight: 1.45 }}>
                          {getContextInsightDisplayText(item)}
                        </Typography>
                      </Box>
                    )) : (
                      <Typography sx={{ fontSize: '0.73rem', color: '#94a3b8' }}>{t('alerts.detail.empty.noContext')}</Typography>
                    )}
                  </Stack>
                </Box>

                <Box sx={{ px: 1.2, py: 1.05, border: '1px solid #e2e8f0', borderRadius: 1.5, bgcolor: '#fff' }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: '#334155', mb: 0.6 }}>
                    {t('alerts.detail.sections.reasons')}
                  </Typography>
                  <Stack spacing={0.45}>
                    {(selectedAlert.reasons || []).length ? selectedAlert.reasons.map((reason, index) => (
                      <Box key={`${index}-${getReasonDisplayText(reason)}`} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.55 }}>
                        <FiberManualRecord sx={{ fontSize: 5, color: colors.success.main, mt: '6px', flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '0.73rem', color: '#475569', lineHeight: 1.45 }}>
                          {getReasonDisplayText(reason)}
                        </Typography>
                      </Box>
                    )) : (
                      <Typography sx={{ fontSize: '0.73rem', color: '#94a3b8' }}>{t('alerts.detail.empty.noReasons')}</Typography>
                    )}
                  </Stack>
                </Box>

                <Box sx={{ px: 1.2, py: 1.05, border: '1px solid #e2e8f0', borderRadius: 1.5, bgcolor: '#fff' }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: '#334155', mb: 0.6 }}>
                    {t('alerts.detail.sections.signals')}
                  </Typography>
                  {Object.keys(selectedAlert.signals || {}).length ? (
                    <Stack spacing={1}>
                      {getDetailedSignalGroups(selectedAlert).map((group) => (
                        <Box key={group.key}>
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.45,
                              px: 0.8,
                              py: 0.28,
                              mb: 0.5,
                              borderRadius: 999,
                              bgcolor: alpha(group.color, 0.09),
                            }}
                          >
                            <FiberManualRecord sx={{ fontSize: 6, color: group.color }} />
                            <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: group.color }}>
                              {group.label} ({group.items.length})
                            </Typography>
                          </Box>

                          <Stack spacing={0}>
                            {group.items.map((item, index) => (
                              <Box
                                key={item.key}
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'flex-start',
                                  gap: 1,
                                  py: 0.62,
                                  borderBottom: index === group.items.length - 1 ? 'none' : '1px solid #f1f5f9',
                                }}
                              >
                                <Box sx={{ minWidth: 0, pr: 1 }}>
                                  <Typography sx={{ fontSize: '0.72rem', color: '#334155', fontWeight: 700, lineHeight: 1.3 }}>
                                    {item.meta.label}
                                  </Typography>
                                  {item.meta.hint ? (
                                    <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', lineHeight: 1.35, mt: 0.12 }}>
                                      {item.meta.hint}
                                    </Typography>
                                  ) : null}
                                </Box>
                                <Box sx={{ textAlign: 'right', flexShrink: 0, pt: 0.05 }}>
                                  <Typography sx={{ fontSize: '0.71rem', fontWeight: 800, color: item.meta.color, lineHeight: 1.25 }}>
                                    {item.meta.primaryValue}
                                  </Typography>
                                  {item.meta.secondaryValue ? (
                                    <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8', mt: 0.08, lineHeight: 1.2 }}>
                                      {item.meta.secondaryValue}
                                    </Typography>
                                  ) : null}
                                </Box>
                              </Box>
                            ))}
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Typography sx={{ fontSize: '0.73rem', color: '#94a3b8' }}>{t('alerts.detail.empty.noSignals')}</Typography>
                  )}
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, pt: 0.2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<OpenInNew sx={{ fontSize: 16 }} />}
                    onClick={() => handleStockClick(selectedAlert.ticker)}
                    sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1.5 }}
                  >
                    {t('alerts.detail.actions.openStock')}
                  </Button>
                  <Button
                    variant="text"
                    onClick={() => setSelectedAlert(null)}
                    sx={{ textTransform: 'none', fontWeight: 700, color: '#64748b' }}
                  >
                    {t('common.close')}
                  </Button>
                </Box>
              </Stack>
            </Box>
          </Box>
        ) : null}
      </Drawer>
    </>
  );
};

export default AlertsTableView;

